import React, { useState, useEffect } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { CameraFill, XLg } from 'react-bootstrap-icons';
import { stockItemAPI } from '../api/stockItemAPI';

// iOS detection utility
const isIOSDevice = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Get maximum scanning frame dimensions based on screen size
const getMaxFrameDimensions = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Reserve space for header (80px) and bottom controls (150px)
    const availableHeight = screenHeight - 230;
    const availableWidth = screenWidth - 40; // 20px margin on each side
    
    // Use 80% of available space to keep some breathing room
    const maxWidth = Math.min(availableWidth * 0.8, 800); // Cap at 800px
    const maxHeight = Math.min(availableHeight * 0.7, 500); // Cap at 500px
    
    // Ensure minimum sizes for usability
    const width = Math.max(maxWidth, 280);
    const height = Math.max(maxHeight, 180);
    
    return { 
        width: Math.floor(width), 
        height: Math.floor(height) 
    };
};

function BarcodeScanner() {
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [availableCameras, setAvailableCameras] = useState([]);
    const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
    const [scanConfidence, setScanConfidence] = useState({ code: null, count: 0, lastScan: 0 });
    const [frameDimensions, setFrameDimensions] = useState(getMaxFrameDimensions());
    const scannerRef = React.useRef(null);
    const restartTimeoutRef = React.useRef(null);
    const isRestartingRef = React.useRef(false);
    const activeStreamsRef = React.useRef([]);

    // Get scan mode from location state (default: 'receive')
    const scanMode = location.state?.mode || 'receive';

    // Configuration for scan reliability
    const REQUIRED_SCANS = 1; // Number of identical scans required
    const SCAN_TIMEOUT_MS = 2000; // Reset if scans are too far apart

    // Validate barcode checksum for common formats
    const validateChecksum = (barcode) => {
        // EAN-13 checksum validation
        if (barcode.length === 13 && /^\d+$/.test(barcode)) {
            const digits = barcode.split('').map(Number);
            const checksum = digits[12];
            let sum = 0;
            for (let i = 0; i < 12; i++) {
                sum += digits[i] * (i % 2 === 0 ? 1 : 3);
            }
            const calculated = (10 - (sum % 10)) % 10;
            return calculated === checksum;
        }

        // UPC-A checksum validation
        if (barcode.length === 12 && /^\d+$/.test(barcode)) {
            const digits = barcode.split('').map(Number);
            const checksum = digits[11];
            let sum = 0;
            for (let i = 0; i < 11; i++) {
                sum += digits[i] * (i % 2 === 0 ? 3 : 1);
            }
            const calculated = (10 - (sum % 10)) % 10;
            return calculated === checksum;
        }

        // If not a checksum-enabled format, assume valid
        return true;
    };

    useEffect(() => {
        let mounted = true;

        // Initialize cameras and start scanning automatically
        const init = async () => {
            if (mounted) {
                await initializeScanner();
            }
        };

        init();

        // Handle window resize to maximize frame on orientation changes
        const handleResize = () => {
            setFrameDimensions(getMaxFrameDimensions());
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        // Cleanup on unmount
        return () => {
            mounted = false;
            // Clear any pending restart timeouts
            if (restartTimeoutRef.current) {
                clearTimeout(restartTimeoutRef.current);
                restartTimeoutRef.current = null;
            }
            stopScanning();
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    const initializeScanner = async () => {
        try {
            setLoading(true);

            // Get available cameras
            const cameras = await Html5Qrcode.getCameras();
            setAvailableCameras(cameras);

            if (cameras.length > 0) {
                // Try to find back camera first
                const backCameraIndex = cameras.findIndex(camera =>
                    camera.label.toLowerCase().includes('back') ||
                    camera.label.toLowerCase().includes('rear') ||
                    camera.label.toLowerCase().includes('environment')
                );

                const selectedIndex = backCameraIndex !== -1 ? backCameraIndex : 0;
                setCurrentCameraIndex(selectedIndex);

                // Start scanning automatically (pass true for isInitialLoad)
                await startScanning(cameras[selectedIndex].id, true);

                setLoading(false);
            } else {
                setLoading(false);
                setError('No cameras found. Please check your device permissions.');
            }
        } catch (error) {
            console.error('Error initializing scanner:', error);
            setLoading(false);
            setError('Failed to access camera. Please grant camera permissions and reload the page.');
        }
    };

    const startScanning = async (cameraId, isInitialLoad = false) => {
        // Prevent concurrent restart operations (but allow initial load)
        if (!isInitialLoad && isRestartingRef.current) {
            return;
        }
        
        if (!isInitialLoad) {
            isRestartingRef.current = true;
        }
        
        // Clear any pending restart timeouts
        if (restartTimeoutRef.current) {
            clearTimeout(restartTimeoutRef.current);
            restartTimeoutRef.current = null;
        }

        // Don't show errors on initial load, only on camera swap
        const shouldShowErrors = !isInitialLoad;

        // Clear any previous errors
        setError(null);

        try {
            // Clear existing scanner
            if (scannerRef.current) {
                try {
                    // Try to get state, but don't fail if we can't
                    let state = -1;
                    try {
                        state = scannerRef.current.getState();
                    } catch (stateError) {
                        // Ignore state error
                    }

                    // State 2 = SCANNING, only stop if actually scanning or if we can't determine state
                    if (state === 2 || state === -1) {
                        try {
                            await scannerRef.current.stop();
                        } catch (stopError) {
                            // Ignore stop error
                        }
                    }

                    try {
                        await scannerRef.current.clear();
                    } catch (clearError) {
                        // Ignore clear error
                    }
                } catch (e) {
                    // Ignore cleanup error
                }
                scannerRef.current = null;

                // Wait for camera to be released
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Create new scanner with custom config
            const isIOS = isIOSDevice();
            const config = {
                qrbox: {
                    width: frameDimensions.width,
                    height: frameDimensions.height,
                },
                fps: isIOS ? 10 : 15,
                formatsToSupport: [
                    Html5QrcodeSupportedFormats.EAN_13,
                    Html5QrcodeSupportedFormats.EAN_8,
                    Html5QrcodeSupportedFormats.UPC_A,
                    Html5QrcodeSupportedFormats.UPC_E,
                    Html5QrcodeSupportedFormats.CODE_128,
                    Html5QrcodeSupportedFormats.CODE_39,
                    Html5QrcodeSupportedFormats.CODE_93,
                    Html5QrcodeSupportedFormats.AZTEC,
                    Html5QrcodeSupportedFormats.CODABAR,
                    Html5QrcodeSupportedFormats.ITF,
                    Html5QrcodeSupportedFormats.MAXICODE,
                    Html5QrcodeSupportedFormats.DATA_MATRIX,
                ],
                experimentalFeatures: {
                    useBarCodeDetectorIfSupported: !isIOS
                },
            };

            const scanner = new Html5Qrcode('reader');
            scannerRef.current = scanner;

            const success = async (decodedText) => {
                const now = Date.now();

                // Validate checksum first
                if (!validateChecksum(decodedText)) {
                    return; // Continue scanning
                }

                // Check if this is the same barcode as before
                setScanConfidence(prev => {
                    const timeSinceLastScan = now - prev.lastScan;

                    // Reset if timeout exceeded or different code
                    if (timeSinceLastScan > SCAN_TIMEOUT_MS || prev.code !== decodedText) {
                        return { code: decodedText, count: 1, lastScan: now };
                    }

                    // Same code within timeout - increment count
                    const newCount = prev.count + 1;

                    // Check if we have enough confidence
                    if (newCount >= REQUIRED_SCANS) {
                        // Process the barcode
                        processBarcode(decodedText, scanner, cameraId);
                        return { code: null, count: 0, lastScan: 0 }; // Reset
                    }

                    return { code: decodedText, count: newCount, lastScan: now };
                });
            };

            const processBarcode = async (decodedText, scanner, cameraId) => {
                try {
                    console.log("Decoded text:", decodedText);

                    // GTIN or UPC mode: Just return the scanned barcode without API validation
                    if (scanMode === 'gtin' || scanMode === 'upc') {
                        const isIOS = isIOSDevice();

                        // Clear any errors before navigation
                        setError(null);

                        // IMPORTANT: Fully stop scanner before navigation
                        await stopScanning();

                        // iOS needs significant extra time to fully release camera before navigation
                        await new Promise(resolve => setTimeout(resolve, isIOS ? 1200 : 100));

                        // Get return path from state or fallback to -1
                        const returnPath = location.state?.returnPath;

                        if (returnPath) {
                            // Navigate to specific return path with scanned value
                            const stateKey = scanMode === 'gtin' ? 'scannedGtin' : 'scannedUpc';
                            navigate(returnPath, {
                                state: { [stateKey]: decodedText },
                                replace: true
                            });
                        } else {
                            // Fallback to navigate back
                            navigate(-1);
                        }
                        return;
                    }

                    // Receive mode: Original behavior - lookup stock item
                    const response = await stockItemAPI.getAll(undefined, undefined, decodedText);
                    console.log("Response:", response);
                    if (!Array.isArray(response)) {
                        throw new Error('Unexpected response from API');
                    }
                    if (response.length === 1) {
                        console.log("Navigating to receive-inventory", response[0]._id);
                        const isIOS = isIOSDevice();

                        // Clear any errors before navigation
                        setError(null);

                        // IMPORTANT: Fully stop scanner before navigation
                        await stopScanning();

                        // iOS needs significant extra time to fully release camera before navigation
                        await new Promise(resolve => setTimeout(resolve, isIOS ? 1200 : 100));

                        // Replace the scanner in history so "back" goes to the page before scanner
                        navigate(`/inventory-receive/${response[0]._id}`, {
                            state: { fromScanner: true },
                            replace: true
                        });
                    } else if (response.length === 0) {
                        setError(`Item '${decodedText}' not found.`);
                        setScanConfidence({ code: null, count: 0, lastScan: 0 });
                        // Restart scanning after error (use ref to prevent multiple concurrent restarts)
                        if (restartTimeoutRef.current) {
                            clearTimeout(restartTimeoutRef.current);
                        }
                        restartTimeoutRef.current = setTimeout(() => {
                            restartTimeoutRef.current = null;
                            startScanning(cameraId, isInitialLoad);
                        }, 2000);
                    }
                } catch (error) {
                    if (error.response && error.response.status === 404) {
                        console.log("Stock item not found in database");
                        setError(`Item '${decodedText}' not found.`);
                    } else {
                        setError(`Error fetching data: ${error.message}`);
                    }
                    setScanConfidence({ code: null, count: 0, lastScan: 0 });
                    // Restart scanning after error (use ref to prevent multiple concurrent restarts)
                    if (restartTimeoutRef.current) {
                        clearTimeout(restartTimeoutRef.current);
                    }
                    restartTimeoutRef.current = setTimeout(() => {
                        restartTimeoutRef.current = null;
                        startScanning(cameraId, isInitialLoad);
                    }, 2000);
                }
            };

            const errorCallback = (err) => {
                // Ignore scanning errors (they're normal when no barcode is visible)
                console.debug('Scanner error:', err);
            };

            await scanner.start(cameraId, config, success, errorCallback);
            
            // Track active streams for cleanup
            try {
                const videoElements = document.querySelectorAll('#reader video');
                videoElements.forEach(video => {
                    if (video.srcObject && !activeStreamsRef.current.includes(video.srcObject)) {
                        activeStreamsRef.current.push(video.srcObject);
                    }
                });
            } catch (e) {
                console.error('Error tracking streams:', e);
            }

            // Verify scanner actually started by checking state after a brief moment
            await new Promise(resolve => setTimeout(resolve, 100));
            const finalState = scanner.getState();

            // Only show error if scanner is not actually scanning
            if (finalState !== 2) {
                if (shouldShowErrors) {
                    setError('Failed to start camera. Please check permissions and reload the page.');
                }
            } else {
                setError(null); // Clear any previous errors
            }
        } catch (error) {
            console.error('Error starting scanner:', error);

            // Check if scanner actually started despite the error
            try {
                await new Promise(resolve => setTimeout(resolve, 100));
                const state = scannerRef.current?.getState();
                if (state !== 2) {
                    if (shouldShowErrors) {
                        setError('Failed to start camera. Please check permissions and reload the page.');
                    }
                } else {
                    setError(null); // Clear the error since scanner is actually working
                }
            } catch (checkError) {
                if (shouldShowErrors) {
                    setError('Failed to start camera. Please check permissions and reload the page.');
                }
            }
        } finally {
            // Reset the restarting flag (but only if this wasn't initial load)
            if (!isInitialLoad) {
                isRestartingRef.current = false;
            }
        }
    };

    const stopScanning = async () => {
        const isIOS = isIOSDevice();
        
        try {
            // STEP 1: Stop all tracked streams IMMEDIATELY
            activeStreamsRef.current.forEach(stream => {
                if (stream) {
                    const tracks = stream.getTracks();
                    tracks.forEach(track => {
                        if (track.readyState === 'live') {
                            track.stop();
                        }
                    });
                }
            });
            activeStreamsRef.current = [];
            
            // STEP 2: Stop the scanner
            if (scannerRef.current) {
                try {
                    const state = scannerRef.current.getState();
                    if (state === 2) {
                        await scannerRef.current.stop();
                    }
                    await scannerRef.current.clear();
                } catch (error) {
                    console.error('Error stopping scanner:', error);
                }
                scannerRef.current = null;
            }
            
            // STEP 3: Search and destroy ALL video elements and their streams
            const allVideos = document.querySelectorAll('video');
            
            allVideos.forEach((video) => {
                try {
                    video.pause();
                    
                    if (video.srcObject) {
                        const stream = video.srcObject;
                        const tracks = stream.getTracks();
                        tracks.forEach(track => {
                            if (track.readyState === 'live') {
                                track.stop();
                            }
                        });
                        video.srcObject = null;
                    }
                    video.remove();
                } catch (videoError) {
                    console.error('Error cleaning video:', videoError);
                }
            });
            
            // STEP 4: Clear reader div
            const readerDiv = document.getElementById('reader');
            if (readerDiv) {
                readerDiv.innerHTML = '';
                readerDiv.offsetHeight; // Force reflow
            }
            
            // STEP 5: Wait for OS to release camera
            if (isIOS) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            
        } catch (error) {
            console.error('stopScanning error:', error);
        }
    };

    const handleSwitchCamera = async () => {
        if (availableCameras.length <= 1) return;

        const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
        setCurrentCameraIndex(nextIndex);
        await startScanning(availableCameras[nextIndex].id);
    };

    const handleClose = async () => {
        const isIOS = isIOSDevice();
        
        await stopScanning();
        
        // iOS needs significant extra time to fully release camera before navigation
        await new Promise(resolve => setTimeout(resolve, isIOS ? 1200 : 100));
        
        navigate(-1);
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#000',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header with close button */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10000,
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%)'
            }}>
                <h3 style={{ color: 'white', margin: 0, fontWeight: '500' }}>
                    {scanMode === 'gtin' ? 'Scan GTIN Barcode' : scanMode === 'upc' ? 'Scan UPC Barcode' : 'Scan Barcode'}
                </h3>
                <Button
                    variant="link"
                    onClick={handleClose}
                    style={{
                        color: 'white',
                        padding: '8px',
                        fontSize: '24px',
                        textDecoration: 'none'
                    }}
                >
                    <XLg />
                </Button>
            </div>

            {/* Loading overlay */}
            {loading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    zIndex: 10001
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            width: '60px',
                            height: '60px',
                            border: '4px solid rgba(255, 255, 255, 0.3)',
                            borderTop: '4px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px'
                        }} />
                        <p style={{ color: 'white', fontSize: '16px', margin: 0 }}>
                            Initializing camera...
                        </p>
                    </div>
                </div>
            )}

            {/* Camera view - full screen */}
            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
            }}>
                <div
                    id="reader"
                    style={{
                        width: '100%',
                        height: '100%',
                        maxWidth: '100vw',
                        maxHeight: '100vh'
                    }}
                />

                {/* Custom Scanning Frame Overlay */}
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: `${frameDimensions.width}px`,
                    height: `${frameDimensions.height}px`,
                    pointerEvents: 'none',
                    zIndex: 9999
                }}>
                    {/* Top-left corner */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '50px',
                        height: '50px',
                        borderTop: '4px solid #00ff00',
                        borderLeft: '4px solid #00ff00',
                        borderTopLeftRadius: '8px',
                        boxShadow: '0 0 15px rgba(0, 255, 0, 0.6)'
                    }} />
                    
                    {/* Top-right corner */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '50px',
                        height: '50px',
                        borderTop: '4px solid #00ff00',
                        borderRight: '4px solid #00ff00',
                        borderTopRightRadius: '8px',
                        boxShadow: '0 0 15px rgba(0, 255, 0, 0.6)'
                    }} />
                    
                    {/* Bottom-left corner */}
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '50px',
                        height: '50px',
                        borderBottom: '4px solid #00ff00',
                        borderLeft: '4px solid #00ff00',
                        borderBottomLeftRadius: '8px',
                        boxShadow: '0 0 15px rgba(0, 255, 0, 0.6)'
                    }} />
                    
                    {/* Bottom-right corner */}
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '50px',
                        height: '50px',
                        borderBottom: '4px solid #00ff00',
                        borderRight: '4px solid #00ff00',
                        borderBottomRightRadius: '8px',
                        boxShadow: '0 0 15px rgba(0, 255, 0, 0.6)'
                    }} />

                    {/* Scanning line animation */}
                    <div className="scan-line" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: '#00ff00',
                        boxShadow: '0 0 10px rgba(0, 255, 0, 0.8)',
                        animation: 'scanAnimation 2s ease-in-out infinite'
                    }} />
                </div>

                {/* Instruction text */}
                {!loading && scanConfidence.count === 0 && (
                    <div style={{
                        position: 'absolute',
                        top: `calc(50% + ${frameDimensions.height / 2 + 30}px)`,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        textAlign: 'center',
                        zIndex: 10002,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        Position barcode within the frame
                    </div>
                )}

                {/* Scan progress indicator - only show if multiple scans required */}
                {scanConfidence.count > 0 && REQUIRED_SCANS > 1 && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 255, 0, 0.9)',
                        color: 'white',
                        padding: '16px 24px',
                        borderRadius: '8px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        zIndex: 10002,
                        boxShadow: '0 4px 20px rgba(0, 255, 0, 0.4)'
                    }}>
                        Scanning {scanConfidence.count}/{REQUIRED_SCANS}
                        <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>
                            Hold steady...
                        </div>
                    </div>
                )}
            </div>

            {/* Add spinner animation and hide duplicate scanner UI */}
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes scanAnimation {
                    0% {
                        top: 0;
                        opacity: 0.8;
                    }
                    50% {
                        opacity: 1;
                    }
                    100% {
                        top: 100%;
                        opacity: 0.8;
                    }
                }

                /* Hide html5-qrcode default UI elements */
                #reader__header_message {
                    display: none !important;
                }

                #reader__dashboard_section {
                    display: none !important;
                }

                #reader__dashboard_section_csr {
                    display: none !important;
                }

                #reader__scan_region {
                    border: none !important;
                    background: transparent !important;
                }
                
                /* Hide ALL children of scan region except video */
                #reader__scan_region > *:not(video) {
                    display: none !important;
                }

                /* Make video fill the container */
                #reader video {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                }

                /* Hide the default scanning box overlay (white frame) - multiple selectors */
                #reader__scan_region img,
                #reader__scan_region svg,
                #reader__scan_region canvas,
                #reader__scan_region div[style*="border"] {
                    display: none !important;
                    opacity: 0 !important;
                    visibility: hidden !important;
                }
                
                /* Target qrbox specifically */
                #qr-shaded-region {
                    display: none !important;
                }

                /* Ensure our custom frame shows above the library's elements */
                #reader canvas {
                    position: relative !important;
                    z-index: 1 !important;
                }
            `}</style>

            {/* Bottom controls */}
            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 10000,
                padding: '20px',
                background: 'linear-gradient(0deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%)'
            }}>
                {/* Error message */}
                {error && (
                    <div style={{
                        backgroundColor: 'rgba(220, 53, 69, 0.9)',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        fontSize: '14px'
                    }}>
                        {error}
                        {isIOSDevice() && (
                            <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.9 }}>
                                <strong>iOS Users:</strong> Try refreshing or use Safari browser
                            </div>
                        )}
                    </div>
                )}

                {/* Camera switch button */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    {availableCameras.length > 1 && (
                        <Button
                            onClick={handleSwitchCamera}
                            style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                border: '2px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <CameraFill size={24} color="white" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BarcodeScanner;
